import { useState, useEffect } from "react";
import Container from "@material-ui/core/Container";
import { Box, Button, TextField } from "@material-ui/core";
import {
  useGetOperation,
  useCreateOperation,
  useUpdateOperation,
} from "../api/api";
import { useHistory } from "react-router-dom";
import { MainMenuToolbar } from "../components/MainMenuToolbar";
import TextFieldWithError from "../components/TextFieldWithError";
import { DateTimePicker } from "@mui/lab";
import type { ShipAndOptionalOperationRouteProps } from "../types/routes";
import type { ApiError, OperationPayload } from "../types/models";

type MutationLike = {
  mutate: (payload: OperationPayload) => void;
};

export const NewMooring = (props: ShipAndOptionalOperationRouteProps) => {
  const shipId = props.match.params.shipId;
  const history = useHistory();
  const [error, setError] = useState<ApiError | undefined>();

  const createOperation = useCreateOperation(shipId, {
    onSuccess: ((operation: OperationPayload) => {
      history.push(`/ships/${shipId}/operations/${operation.id}`);
    }) as () => void,
    onError: ((err: ApiError) => setError(err)) as () => void,
  }) as unknown as MutationLike;
  const [operation, setOperation] = useState<OperationPayload>({});

  return (
    <Partial
      operation={operation}
      setOperation={setOperation}
      error={error}
      title="Start New Mooring operation"
      backLink={`/ships/${shipId}/operations/`}
      onClick={() => {
        createOperation.mutate(operation);
      }}
    />
  );
};

export const EditMooring = (props: ShipAndOptionalOperationRouteProps) => {
  const { shipId, operationId } = props.match.params;
  const history = useHistory();
  const [error, setError] = useState<ApiError | undefined>();

  const { data: operationFromApi } = useGetOperation(
    shipId,
    operationId || ""
  ) as { data?: OperationPayload };

  const updateOperation = useUpdateOperation(shipId, operationId, {
    onSuccess: ((operation: OperationPayload) => {
      history.push(`/ships/${shipId}/operations/${operation.id}`);
    }) as () => void,
    onError: ((err: ApiError) => setError(err)) as () => void,
  }) as unknown as MutationLike;

  const [operation, setOperation] = useState<OperationPayload>({});

  useEffect(() => {
    if (operationFromApi) {
      setOperation({
        ...operationFromApi,
        firstLine: operationFromApi.first_line,
      });
    }
  }, [operationFromApi]);

  return (
    <Partial
      operation={operation}
      setOperation={setOperation}
      error={error}
      title="Edit Mooring operation"
      backLink={`/ships/${shipId}/operations/${operationId}`}
      onClick={() => {
        updateOperation.mutate(operation);
      }}
    />
  );
};

const Partial = ({
  operation,
  setOperation,
  error,
  title,
  backLink,
  onClick
}: {
  operation: OperationPayload;
  setOperation: (value: OperationPayload) => void;
  error: ApiError | undefined;
  title: string;
  backLink: string;
  onClick: () => void;
}) => {
  return (
    <div>
      <MainMenuToolbar title={title} backLink={backLink} />
      <Container maxWidth="sm">
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <TextFieldWithError
            label="Country"
            objectKey="country"
            error={error}
            editedCable={operation}
            setEditedCable={setOperation}
          />
          <TextFieldWithError
            label="Port"
            objectKey="port"
            error={error}
            editedCable={operation}
            setEditedCable={setOperation}
          />
          <TextFieldWithError
            label="Terminal"
            objectKey="terminal"
            error={error}
            editedCable={operation}
            setEditedCable={setOperation}
          />
          <DateTimePicker
            label="First line"
            value={operation.firstLine || null}
            onChange={(x) => {
              setOperation({
                ...operation,
                firstLine: x,
              });
            }}
            renderInput={(params) => (
              // @ts-expect-error MUI v4/v5 TextField prop type mismatch
              <TextField
                {...params}
                error={error?.message?.["firstLine"] !== undefined}
                helperText={error?.message?.["firstLine"] as string | undefined}
                sx={{ flex: "0 0 48%", mt: 2 }}
                variant="standard"
              />
            )}
          />
          {/* <DateTimePicker
            label="Moored"
            value={operation.moored || null}
            onChange={(x) => {
              setOperation({
                ...operation,
                moored: x,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={error?.message?.moored !== undefined}
                helperText={error?.message?.moored}
                sx={{ flex: "0 0 48%", mt: 2 }}
                variant="standard"
              />
            )}
          /> */}
          <DateTimePicker
            label="Unmoored"
            value={operation.completed || null}
            onChange={(x) => {
              setOperation({
                ...operation,
                completed: x,
              });
            }}
            renderInput={(params) => (
              // @ts-expect-error MUI v4/v5 TextField prop type mismatch
              <TextField
                {...params}
                error={error?.message?.["completed"] !== undefined}
                helperText={error?.message?.["completed"] as string | undefined}
                sx={{ flex: "0 0 48%", mt: 2 }}
                variant="standard"
              />
            )}
          />
        </Box>
        <Button
          disabled={!operation.port || !operation.country}
          onClick={onClick}
          variant="contained"
          sx={{ mt: 4 }}
        >
          Add
        </Button>
      </Container>
    </div>
  );
};
